import React, { useState, useMemo, useCallback, useEffect } from "react";
import { ReactComponent as GreenEye } from "../../../assets/svgs/greenEye.svg";
import SearchBar from "../../../components/Elements/Search/SearchBar";
import { Tables } from "../../../components/Elements/Tables/Tables";
import CardsBorder from "../../../components/Cards/CardsBorder";
import { putApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import CustomSwitch from "../../../components/Elements/CustomSwitch/CustomSwitch";
import GroupAddUser from "../DailogBoxes/GroupAddUser";
import ImportUsers from "../DailogBoxes/ImportUsers";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";
import { useNavigate } from "react-router-dom";
import EditUsersDialog from "../../Users/DailogBoxes/EditUsers";
import { ReactComponent as EditIcon } from "../../../assets/svgs/greenEdit.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/svgs/deleteIcon.svg";
import { fetchGroupUsers, revokeViewAccess } from "../../../redux/group/groupSlice";
import { useDispatch, useSelector } from "react-redux";

const GroupUserTable = ({ clientId }) => {
  const dispatch = useDispatch();
  const { groupUsers, loading, currentGroup } = useSelector((state) => state.group);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [tableData, setTablecData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [openAddUser, setOpenAddUser] = useState(false);
  const [openimportUser, setOpenImportUser] = useState(false);
  const [isSuccess, setIsSuccess] = useState(true);
  const [statusMessage, setStatusMessage] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  const [popup, setPopup] = useState({
    visible: false,
    type: "success",
    title: "",
    message: "",
    showActions: false,
    onConfirm: null,
    onCancel: null,
    confirmText: "Confirm",
    cancelText: "Cancel",
  });

  const pageSize = 5;
  const [sortOrder, setSortOrder] = useState("-createdAt");

  const navigate = useNavigate();

  const handleRevokeAccess = (userClientId, userName) => {
    // Hide the confirmation popup first
    setPopup((p) => ({ ...p, visible: false }));

    dispatch(revokeViewAccess({ groupId: clientId, clientId: userClientId }))
      .unwrap()
      .then(() => {
        setPopup({
          visible: true,
          type: "success",
          title: "Deleted",
          message: `${userName} has been deleted successfully!`,
          showActions: false,
        });
        setTimeout(() => {
          setPopup((p) => ({ ...p, visible: false }));
        }, 2000);
        fetchUserData(clientId);
      })
      .catch((err) => {
        setPopup({
          visible: true,
          type: "error",
          title: "Error",
          message: err || "Failed to revoke access.",
          showActions: false,
        });
        setTimeout(() => {
          setPopup((p) => ({ ...p, visible: false }));
        }, 2000);
      });
  };

  const fetchUserData = async (clientId) => {
    if (!clientId) return;
    dispatch(fetchGroupUsers({
      clientId,
      sortOrder,
    }));
  };

  const filteredUsers = useMemo(() => {
    const search = debouncedSearch.trim().toLowerCase();

    if (!search) return groupUsers || [];

    return (groupUsers || []).filter((user) => {
      const userData = user.clientId || user;
      const searchableValues = [
        userData?.organization?.name || "",
        userData?.employee_id || "",
        userData?.userId || "",
        userData?.username || "",
        userData.email,
        Array.isArray(userData.role) ? userData.role.join(" ") : userData.role,
      ];

      return searchableValues.some((value) =>
        String(value || "").toLowerCase().includes(search)
      );
    });
  }, [groupUsers, debouncedSearch]);

  useEffect(() => {
    if (filteredUsers) {
      const startIndex = (currentPage - 1) * pageSize;
      const paginatedUsers = filteredUsers.slice(startIndex, startIndex + pageSize);

      const allUsers = paginatedUsers.map((user, index) => {
        const userData = user.clientId || user; // Support nested clientId or flat user object
        return {
          key: user._id,
          serial: startIndex + index + 1,
          name: <div className="text-center">{userData?.organization?.name || "—"}</div>,
          userId: userData?.employee_id || userData?.userId || "—",
          userName: <div className="text-center">{userData?.username || userData?.employee_id || userData?.userId || userData?.email || "—"}</div>,
          email: <div className="text-center">{userData.email}</div>,
          role: <div className="text-center">{Array.isArray(userData.role) ? userData.role.join(", ") : userData.role || "N/A"}</div>,
          action: (
            <div className="flex gap-4 items-center justify-center">
              {/* <EditIcon
                style={{ fontSize: "18px", cursor: "pointer" }}
                onClick={() => {
                  setSelectedUser(userData);
                  setOpenEditDialog(true);
                }}
              /> */}
              <DeleteIcon
                style={{ fontSize: "18px", cursor: "pointer", color: "#ff4d4f" }}
                onClick={() => {
                  setPopup({
                    visible: true,
                    type: "warning",
                    title: "Confirm Delete",
                    message: `Are you sure you want to delete ${userData.name}?`,
                    showActions: true,
                    confirmText: "Yes, Delete",
                    cancelText: "Cancel",
                    onCancel: () => setPopup((p) => ({ ...p, visible: false })),
                    onConfirm: () => handleRevokeAccess(userData._id, userData.name),
                  });
                }}
              />
            </div>
          ),
        };
      });
      setTablecData(allUsers);
    } else {
      setTablecData([]);
    }
  }, [filteredUsers, currentPage, pageSize]);

  const headers = useMemo(
    () => [
      { label: "Sr. No.", key: "serial" },
      { label: "Name", key: "name" },
      { label: "User Name", key: "userName" },
      { label: "Email", key: "email" },
      { label: "Action", key: "action" },
    ],
    []
  );

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleSort = useCallback(() => {
    setSortOrder((prevOrder) =>
      prevOrder === "createdAt" ? "-createdAt" : "createdAt"
    );
  }, []);

  useEffect(() => {
    fetchUserData(clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, sortOrder]);

  // Debounce search input
  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500); // wait 0.5s after typing stops
    return () => clearTimeout(delay);
  }, [searchTerm]);

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

      const response = await putApi({
        apiUrl: API_URLS.UPDATE_CUSTOMER_USER(userId),
        data: { status: newStatus },
        headers: { client: clientId },
      });
      if (response.success) {
        setStatusMessage(
          `User Management ${newStatus === "Active" ? "Active" : "Inactive"
          } is successfully.`
        );
        setPopup({
          visible: true,
          type: "success",
          title: "Success!!",
          message: "Status changes successfully",
          showActions: false,
        });
        setIsSuccess(newStatus === "Active");
        fetchUserData(clientId);
      } else {
        setPopup({
          visible: true,
          type: "warning",
          title: "Failed in Updating Status",
          message: `${response.message}`,
          showActions: false,
        });
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      setPopup({
        visible: true,
        type: "error",
        title: "Failed !!",
        message: `${error.message}`,
        showActions: false,
      });
    } finally {
      setTimeout(() => {
        setPopup((p) => ({ ...p, visible: false }));
      }, 2000);
    }
  };

  return (
    <div style={{ paddingLeft: "40px", paddingRight: "40px", paddingTop: "20px" }}>
      <CardsBorder>
        <div className="flex justify-between " >
          <div className="flex justify-between my-4 font-medium" >
            <div className="flex gap-8">
              <p className="text-[#06513D] pt-2 px-10 text-xl">User View Access</p>
              <SearchBar
                width="300px"
                onSearch={(value) => setSearchTerm(value)}
              />
            </div>
          </div>
          <div className="flex px-10 gap-2" >
            <CustomButton
              buttonType="primary"
              variant="ok"
              buttonText="+ ADD USERS"
              className="text-sm h-[50px]"
              onClick={() => setOpenAddUser(true)}
            />
          </div>
        </div>

        <Tables
          data={tableData}
          headers={headers}
          pageSize={pageSize}
          total={filteredUsers.length}
          current={currentPage}
          onChange={handlePageChange}
          onSort={handleSort}
          loading={loading}
        />
      </CardsBorder>

      {openAddUser && (
        <GroupAddUser
          onOpen={openAddUser}
          onClose={() => setOpenAddUser(false)}
          clientId={clientId}
          groupName={currentGroup?.groupName}
          groupOffsetId={currentGroup?.groupId}
          validTill={currentGroup?.validTill}
          onSave={() => {
            setOpenAddUser(false);
            setPopup({
              visible: true,
              type: "success",
              title: "User Created",
              message: "User data saved successfully.",
              showActions: false,
            });
            setTimeout(() => {
              setPopup((p) => ({ ...p, visible: false }));
            }, 2000);
            fetchUserData(clientId);
          }}
        />
      )}
      {openimportUser && (
        <ImportUsers
          onOpen={openimportUser}
          onClose={() => setOpenImportUser(false)}
          clientId={clientId}
          onSave={() => {
            setOpenImportUser(false);
            setPopup({
              visible: true,
              type: "success",
              title: "User Created",
              message: "User data saved successfully.",
              showActions: false,
            });
            setTimeout(() => {
              setPopup((p) => ({ ...p, visible: false }));
            }, 2000);
            fetchUserData(clientId);
          }}
        />
      )}

      {openEditDialog && selectedUser && (
        <EditUsersDialog
          onOpen={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          userData={selectedUser}
          onSave={() => {
            setPopup({
              visible: true,
              type: "success",
              title: "User Edited",
              message: "User Edited Successfully",
              showActions: false,
            });
            setTimeout(() => {
              setPopup((p) => ({ ...p, visible: false }));
            }, 2000);
          }}
          onSuccessFalse={(msg) => {
            setPopup({
              visible: true,
              type: "error",
              title: "Failed",
              message: msg || "Failed to edit User",
              showActions: false,
            });
            setTimeout(() => {
              setPopup((p) => ({ ...p, visible: false }));
            }, 2000);
          }}
        />
      )}

      {popup.visible && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
            showActions={popup.showActions}
            confirmText={popup.confirmText}
            cancelText={popup.cancelText}
            onConfirm={popup.onConfirm}
            onCancel={popup.onCancel}
            onClose={() => setPopup((p) => ({ ...p, visible: false }))}
          />
        </div>
      )}
    </div>
  );
};

export default GroupUserTable;
